import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BriefcaseBusiness,
  CalendarClock,
  Check,
  ChevronDown,
  Edit3,
  History,
  MapPin,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  UserRoundCheck,
  UsersRound,
  X,
} from "lucide-react";
import { api, ApiError } from "../api";
import type { ApiList, Mission, MissionCandidate, MissionCatalogs, MissionEvent, User } from "../types";
import { skillLabel } from "../utils";
import {
  candidateHasValidVisa,
  candidateHasLandingEntry,
  candidateVisaLabel,
  filterMissionCandidates,
  missionUnitLabel,
  type CandidateUnitFilter,
} from "../mission-candidates";
import {
  AIRCRAFT_QUALIFICATION_PACKAGES,
  aircraftQualificationPackage,
  aircraftQualificationPackageForPair,
} from "../../shared/aircraft-qualification-packages";
import {
  Alert,
  Button,
  Card,
  Empty,
  Field,
  Loading,
  Modal,
  PageHeader,
  SearchableCombobox,
  SelectField,
  TextareaField,
} from "../components/ui";
import { HardDeleteAction } from "../components/HardDeleteAction";

interface MissionFormState {
  taskName: string;
  taskType: "normal" | "aog";
  countryCode: string;
  provinceCode: string;
  outboundFlightLocal: string;
  aircraftPackageId: string;
  aircraftType: string;
  engineType: string;
  aircraftRegistration: string;
  requiredSkills: string[];
  participantIds: string[];
  publicNote: string;
  managementNote: string;
  overrideReason: string;
}

const statusLabels: Record<Mission["status"], string> = {
  PENDING: "Onay Bekliyor",
  REJECTED: "Reddedildi",
  CANCELLED: "İptal Edildi",
  PLANNED: "Planlandı",
  ACTIVE: "Görevde",
  COMPLETED: "Tamamlandı",
};

const groupLabels: Record<MissionCandidate["shiftGroup"], string> = {
  CURRENT: "Referans anında vardiyada",
  NEXT: "Hemen sonraki vardiya",
  OTHER: "Diğer personeller",
  UNSUITABLE: "Güncel uyarısı olan personeller",
};

const eventLabels: Record<MissionEvent["eventType"], string> = {
  created: "Görev oluşturuldu",
  submitted: "Onaya gönderildi",
  approved: "Onaylandı",
  rejected: "Reddedildi",
  updated: "Görev güncellendi",
  participant_added: "Katılımcı eklendi",
  participant_removed: "Katılımcı çıkarıldı",
  override_confirmed: "Uyarılar gerekçeyle aşıldı",
  cancelled: "Görev iptal edildi",
  manually_completed: "Görev tamamlandı",
};

const changedFieldLabels: Record<string, string> = {
  taskName: "görev adı",
  taskType: "görev türü",
  country: "ülke",
  province: "il",
  taskStart: "görev başlangıcı",
  outboundFlight: "gidiş uçuşu",
  readyAt: "hazır olma zamanı",
  returnDate: "dönüş tarihi",
  returnFlight: "dönüş uçuşu",
  returnUnknown: "dönüş belirsizliği",
  aircraftType: "uçak tipi",
  engineType: "motor tipi",
  aircraftPackageId: "uçak–motor yetkinlik paketi",
  aircraftRegistration: "uçak tescili",
  requiredSkills: "skill listesi",
  publicNote: "genel açıklama",
  managementNote: "yönetim özel notu",
};

function eventChangeSummary(event: MissionEvent): string {
  const changes = event.changes;
  if (!changes) return "";
  const parts: string[] = [];
  if (Array.isArray(changes.changedFields) && changes.changedFields.length) {
    parts.push(`Değişen alanlar: ${changes.changedFields.map((field) => changedFieldLabels[String(field)] ?? String(field)).join(", ")}`);
  }
  if (Array.isArray(changes.participantsAdded) && changes.participantsAdded.length) parts.push(`${changes.participantsAdded.length} katılımcı eklendi`);
  if (Array.isArray(changes.participantsRemoved) && changes.participantsRemoved.length) parts.push(`${changes.participantsRemoved.length} katılımcı çıkarıldı`);
  if (typeof changes.actualReturnAt === "string") parts.push(`Gerçek dönüş: ${dateTimeLabel(changes.actualReturnAt)}`);
  if (typeof changes.durationMinutes === "number") parts.push(`Görev süresi: ${durationLabel(changes.durationMinutes)}`);
  return parts.join(" · ");
}

function localParts(iso: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
}

function localInput(iso: string | null): string {
  if (!iso) return "";
  const value = localParts(iso);
  return `${value.year}-${value.month}-${value.day}T${value.hour}:${value.minute}`;
}

function dateTimeLabel(iso: string): string {
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

function durationLabel(totalMinutes: number): string {
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  return [days ? `${days} gün` : "", hours ? `${hours} saat` : "", minutes ? `${minutes} dk` : ""]
    .filter(Boolean)
    .join(" ") || "0 dk";
}

function freshForm(user: User, canManage: boolean): MissionFormState {
  const now = new Date(Date.now() + 60 * 60 * 1000).toISOString();
  const start = localInput(now);
  return {
    taskName: "",
    taskType: "normal",
    countryCode: "TR",
    provinceCode: "",
    outboundFlightLocal: start,
    aircraftPackageId: "",
    aircraftType: "",
    engineType: "",
    aircraftRegistration: "",
    requiredSkills: [],
    participantIds: canManage ? [] : [String(user.id)],
    publicNote: "",
    managementNote: "",
    overrideReason: "",
  };
}

function missionForm(mission: Mission): MissionFormState {
  const revision = mission.pendingRevision ?? mission.revision;
  const qualificationPackage = aircraftQualificationPackageForPair(revision.aircraftType, revision.engineType);
  return {
    taskName: revision.taskName ?? "",
    taskType: revision.taskType.toLowerCase() as "normal" | "aog",
    countryCode: revision.countryCode,
    provinceCode: revision.provinceCode ?? "",
    outboundFlightLocal: localInput(revision.outboundFlightAt),
    aircraftPackageId: revision.aircraftPackageId
      ?? qualificationPackage?.id
      ?? (revision.aircraftType || revision.engineType ? "__legacy__" : ""),
    aircraftType: revision.aircraftType,
    engineType: revision.engineType,
    aircraftRegistration: revision.aircraftRegistration ?? "",
    requiredSkills: revision.requiredSkills,
    participantIds: revision.participants.map((person) => String(person.id)),
    publicNote: revision.publicNote ?? "",
    managementNote: revision.managementNote ?? "",
    overrideReason: "",
  };
}

function payload(form: MissionFormState, overrideConfirmed: boolean) {
  return {
    taskName: form.taskName.trim() || null,
    taskType: form.taskType,
    countryCode: form.countryCode,
    provinceCode: form.countryCode === "TR" ? form.provinceCode || null : null,
    outboundFlightLocal: form.outboundFlightLocal,
    aircraftPackageId: form.aircraftPackageId && form.aircraftPackageId !== "__legacy__"
      ? form.aircraftPackageId
      : null,
    aircraftType: form.aircraftPackageId === "__legacy__" ? form.aircraftType.trim() : "",
    engineType: form.aircraftPackageId === "__legacy__" ? form.engineType.trim() : "",
    aircraftRegistration: form.aircraftRegistration.trim() || null,
    requiredSkills: form.requiredSkills,
    participantIds: form.participantIds,
    publicNote: form.publicNote.trim() || null,
    managementNote: form.managementNote.trim() || null,
    overrideConfirmed,
    overrideReason: overrideConfirmed ? form.overrideReason.trim() || null : null,
  };
}

function extractWarnings(error: ApiError): string[] {
  if (error.status !== 409 || typeof error.details !== "object" || error.details === null) return [];
  const warnings = (error.details as { warnings?: Array<{ displayName?: string; message?: string }> }).warnings;
  return warnings?.map((warning) => `${warning.displayName ? `${warning.displayName}: ` : ""}${warning.message ?? "Güncel personel uyarısı"}`) ?? [];
}

export function MissionsScreen({
  currentUser,
  notify,
}: {
  currentUser: User;
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
}) {
  const [catalogs, setCatalogs] = useState<MissionCatalogs | null>(null);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Mission | null>(null);
  const [form, setForm] = useState<MissionFormState>(() => freshForm(currentUser, false));
  const [candidates, setCandidates] = useState<MissionCandidate[]>([]);
  const [finding, setFinding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidateUnit, setCandidateUnit] = useState<CandidateUnitFilter>("ALL");
  const [showUnsuitable, setShowUnsuitable] = useState(false);
  const [overrideWarnings, setOverrideWarnings] = useState<string[]>([]);
  const [action, setAction] = useState<{ mission: Mission; type: "cancel" | "complete" } | null>(null);
  const [actionReason, setActionReason] = useState("");
  const [actualReturnLocal, setActualReturnLocal] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [historyMission, setHistoryMission] = useState<Mission | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [listFilter, setListFilter] = useState<"active" | "all" | Mission["status"]>("active");
  const [missionSearch, setMissionSearch] = useState("");
  const routeParams = new URLSearchParams(window.location.hash.split("?", 2)[1] ?? "");
  const focusedId = routeParams.get("focus") ?? routeParams.get("mission");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [catalogValue, missionValue] = await Promise.all([
        api<MissionCatalogs>("/api/missions/catalogs"),
        api<ApiList<Mission> & { nextOffset?: number | null }>("/api/missions?limit=100"),
      ]);
      setCatalogs(catalogValue);
      setMissions(missionValue.items);
      const routeParams = new URLSearchParams(window.location.hash.split("?", 2)[1] ?? "");
      if (routeParams.get("create") === "1") {
        setEditing(null);
        setForm(freshForm(currentUser, catalogValue.canManage));
        setFormOpen(true);
      }
      const requested = routeParams.get("mission");
      if (requested) {
        const selected = missionValue.items.find((mission) => mission.id === requested);
        if (selected) setEditing(selected);
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Görevler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (loading || !focusedId) return;
    setListFilter("all");
    window.setTimeout(() => document.getElementById(`mission-${focusedId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
  }, [focusedId, loading]);

  useEffect(() => {
    setCandidates([]);
  }, [
    form.countryCode,
    form.provinceCode,
    form.outboundFlightLocal,
    form.aircraftType,
    form.engineType,
    form.requiredSkills.join("|"),
  ]);

  function openCreate() {
    const canManage = Boolean(catalogs?.canManage);
    setEditing(null);
    setForm(freshForm(currentUser, canManage));
    setCandidates([]);
    setOverrideWarnings([]);
    setCandidateSearch("");
    setCandidateUnit("ALL");
    setShowUnsuitable(false);
    setFormOpen(true);
  }

  function openEdit(mission: Mission) {
    setEditing(mission);
    setForm(missionForm(mission));
    setCandidates([]);
    setOverrideWarnings([]);
    setCandidateSearch("");
    setCandidateUnit("ALL");
    setShowUnsuitable(true);
    setFormOpen(true);
  }

  function setField<K extends keyof MissionFormState>(key: K, value: MissionFormState[K]) {
    setOverrideWarnings([]);
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function findCandidates() {
    setFinding(true);
    setError("");
    try {
      const value = await api<ApiList<MissionCandidate>>("/api/missions/candidates", {
        method: "POST",
        body: payload(form, false),
      });
      setCandidates(value.items);
      if (!catalogs?.canManage) setForm((current) => ({ ...current, participantIds: [String(currentUser.id)] }));
    } catch (caught) {
      notify(caught instanceof ApiError ? caught.message : "Personel önerisi hazırlanamadı.", "danger");
    } finally {
      setFinding(false);
    }
  }

  async function save() {
    if (!form.participantIds.length) {
      notify("En az bir katılımcı seçin.", "danger");
      return;
    }
    if (overrideWarnings.length && form.overrideReason.trim().length < 3) {
      notify("Güncel uyarıları aşmak için kısa bir gerekçe yazın.", "danger");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...payload(form, overrideWarnings.length > 0),
        ...(editing ? { version: editing.version } : {}),
      };
      await api(editing ? `/api/missions/${editing.id}` : "/api/missions", {
        method: editing ? "PATCH" : "POST",
        body,
      });
      notify(catalogs?.canManage ? "Görev kaydedildi." : "Görev onaya gönderildi.");
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError) {
        const warnings = extractWarnings(caught);
        if (warnings.length) {
          setOverrideWarnings(warnings);
          notify("Kaydetme anında yeni uyarılar bulundu. İnceleyip gerekçeyle tekrar kaydedin.", "info");
        } else {
          notify(caught.message, "danger");
        }
      } else {
        notify("Görev kaydedilemedi.", "danger");
      }
    } finally {
      setSaving(false);
    }
  }

  async function runAction() {
    if (!action || (action.type === "cancel" ? actionReason.trim().length < 3 : !actualReturnLocal)) return;
    setActionLoading(true);
    try {
      await api(`/api/missions/${action.mission.id}/${action.type}`, {
        method: "POST",
        body: action.type === "cancel"
          ? { version: action.mission.version, reason: actionReason.trim() }
          : {
              version: action.mission.version,
              actualReturnLocal,
              reason: actionReason.trim() || null,
            },
      });
      notify(action.type === "cancel" ? "Görev iptal edildi." : "Görev tamamlandı.");
      setAction(null);
      setActionReason("");
      setActualReturnLocal("");
      await load();
    } catch (caught) {
      notify(caught instanceof ApiError ? caught.message : "İşlem tamamlanamadı.", "danger");
    } finally {
      setActionLoading(false);
    }
  }

  async function openHistory(mission: Mission) {
    setHistoryMission(mission);
    setHistoryLoading(true);
    try {
      setHistoryMission(await api<Mission>(`/api/missions/${mission.id}`));
    } catch (caught) {
      notify(caught instanceof ApiError ? caught.message : "Görev geçmişi yüklenemedi.", "danger");
      setHistoryMission(null);
    } finally {
      setHistoryLoading(false);
    }
  }

  const visibleCandidates = useMemo(() => {
    return filterMissionCandidates(candidates, {
      unit: candidateUnit,
      search: candidateSearch,
      showUnsuitable,
    });
  }, [candidateSearch, candidateUnit, candidates, showUnsuitable]);
  const visibleMissions = useMemo(() => {
    const term = missionSearch.trim().toLocaleLowerCase("tr-TR");
    const activeStatuses: Mission["status"][] = ["PENDING", "PLANNED", "ACTIVE"];
    const priority: Record<Mission["status"], number> = {
      ACTIVE: 0, PENDING: 1, PLANNED: 2, COMPLETED: 3, REJECTED: 4, CANCELLED: 5,
    };
    return missions
      .filter((mission) => listFilter === "all"
        || (listFilter === "active" ? activeStatuses.includes(mission.status) : mission.status === listFilter))
      .filter((mission) => !term || [
        mission.revision.taskName,
        mission.revision.location,
        mission.revision.countryCode,
        mission.revision.participants.map((person) => person.displayName).join(" "),
      ].filter(Boolean).join(" ").toLocaleLowerCase("tr-TR").includes(term))
      .sort((left, right) => priority[left.status] - priority[right.status]
        || left.revision.outboundFlightAt.localeCompare(right.revision.outboundFlightAt)
        || left.id.localeCompare(right.id));
  }, [listFilter, missionSearch, missions]);

  const aircraftPackageOptions = [
    ...AIRCRAFT_QUALIFICATION_PACKAGES.map((item) => ({ value: item.id, label: item.label })),
    ...(form.aircraftPackageId === "__legacy__"
      ? [{ value: "__legacy__", label: `Mevcut kayıt: ${form.aircraftType} — ${form.engineType}` }]
      : []),
  ];
  const equipmentLabel = (revision: Mission["revision"]) => (
    (revision.aircraftPackageId ? aircraftQualificationPackage(revision.aircraftPackageId)?.label : null)
    ?? [revision.aircraftType, revision.engineType].filter(Boolean).join(" · ")
    ?? "Belirtilmedi"
  );

  const formReady = Boolean(
    form.countryCode && (form.countryCode !== "TR" || form.provinceCode)
      && form.outboundFlightLocal,
  );
  const candidateVisaSections = form.countryCode === "TR"
    ? [{ key: "all", label: null, items: visibleCandidates }]
    : [
      { key: "valid", label: "Geçerli vizesi olanlar", items: visibleCandidates.filter(candidateHasValidVisa) },
      { key: "landing", label: "Landing Card ile görev girişi", items: visibleCandidates.filter(candidateHasLandingEntry) },
      { key: "unknown", label: "Kontrol gerekli", items: visibleCandidates.filter(candidate => candidate.entryEligibility === 'UNKNOWN') },
      { key: "warning", label: "Vize uyarısı olanlar", items: visibleCandidates.filter((candidate) => !candidateHasValidVisa(candidate) && !candidateHasLandingEntry(candidate) && candidate.entryEligibility !== 'UNKNOWN') },
    ];

  function candidateCard(candidate: MissionCandidate) {
    const selected = form.participantIds.includes(candidate.id);
    const visaLabel = candidateVisaLabel(candidate);
    const unitLabel = missionUnitLabel(candidate.organizationUnit);
    return (
      <label className={`candidate-card${selected ? " selected" : ""}`} key={candidate.id}>
        <input type="checkbox" checked={selected} disabled={!catalogs?.canManage && candidate.id !== String(currentUser.id)} onChange={(event) => setField("participantIds", event.target.checked ? [...form.participantIds, candidate.id] : form.participantIds.filter((id) => id !== candidate.id))} />
        <span className="candidate-card__body">
          <strong>{candidate.displayName}</strong>
          <small>{candidate.loginCode} {candidate.mainTeam ? `· ${candidate.mainTeam} Ekibi` : ""} · {candidate.effectiveShift ?? "OFF"}</small>
          <span className="candidate-tags">
            {unitLabel ? <b className={`candidate-tag candidate-tag--unit candidate-tag--${candidate.organizationUnit.toLowerCase()}`}>{unitLabel}</b> : null}
            {form.requiredSkills.includes("B1") && form.aircraftPackageId && form.aircraftPackageId !== "__legacy__" && candidate.aircraftMatch ? <b className="candidate-tag candidate-tag--ok">Uçak/motor eşleşiyor</b> : null}
            {candidate.lastCompletedAt ? <b className="candidate-tag">Son görev: {new Date(candidate.lastCompletedAt).toLocaleDateString("tr-TR")}</b> : <b className="candidate-tag">Kayıtlı tamamlanmış görev yok</b>}
          </span>
          {visaLabel ? (
            <span className={`candidate-visa candidate-visa--${candidateHasValidVisa(candidate) ? "valid" : "warning"}`}>
              {candidateHasValidVisa(candidate) ? <Check size={15} /> : <AlertTriangle size={15} />}
              {visaLabel}
            </span>
          ) : null}
          {candidate.warnings.map((warning) => <span className={`candidate-warning candidate-warning--${warning.severity}`} key={warning.code}><AlertTriangle size={14} />{warning.message}</span>)}
          {form.countryCode !== 'TR' && candidate.landingCard ? <small>Vize: {candidateHasValidVisa(candidate) ? 'Geçerli' : 'Geçerli vize yok'} · Landing Card: {candidate.landingCard.hasLandingCard ? `Var${candidate.landingCard.landingCardExpiryDate ? ` · ${candidate.landingCard.landingCardExpiryDate}` : ''}` : 'Yok'}</small> : null}
        </span>
      </label>
    );
  }

  return (
    <div className="page missions-page">
      <PageHeader
        eyebrow="PERSONEL GÖREV TAKİBİ"
        title="Görevler"
        description="Saha görevlerini planlayın; vardiya, izin ve personel beyanlarını birlikte değerlendirin."
        actions={(
          <>
            <Button variant="ghost" onClick={() => void load()}><RefreshCw size={18} />Yenile</Button>
            <Button onClick={openCreate}><Plus size={18} />Yeni görev</Button>
          </>
        )}
      />
      {catalogs?.actedAsDelegate ? (
        <Alert tone="success" title="Aktif vekâlet">
          Görev işlemlerini vekâleten yapıyorsunuz; gerçek aktör ve adına işlem yaptığınız başteknisyen audit kaydına yazılır.
        </Alert>
      ) : null}
      <div className="mission-list-toolbar" aria-label="Görev listesi filtreleri">
        <label className="mission-list-search">
          <span className="sr-only">Görev ara</span>
          <Search aria-hidden="true" />
          <input value={missionSearch} onChange={(event) => setMissionSearch(event.target.value)} placeholder="Görev, konum veya personel ara" />
        </label>
        <div className="segmented mission-status-filter" role="group" aria-label="Görev durumu">
          {([
            ["active", "Aktif / planlı"], ["PENDING", "Onay bekleyen"], ["COMPLETED", "Tamamlanan"],
            ["CANCELLED", "İptal edilen"], ["all", "Tümü"],
          ] as const).map(([value, label]) => (
            <button type="button" className={listFilter === value ? "active" : ""} aria-pressed={listFilter === value}
              onClick={() => setListFilter(value)} key={value}>{label}</button>
          ))}
        </div>
      </div>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {loading ? <Loading label="Görevler yükleniyor" /> : visibleMissions.length ? (
        <div className="mission-list">
          {visibleMissions.map((mission) => (
            <Card className={`mission-card${String(mission.id) === focusedId ? " record-card--focused" : ""}`} key={mission.id}>
              <span id={`mission-${mission.id}`} className="record-focus-anchor" aria-hidden="true" />
              <header>
                <span className={`mission-status mission-status--${mission.status.toLowerCase()}`}>{statusLabels[mission.status]}</span>
                {mission.revision.taskType === "AOG" ? <b className="aog-badge">ACİL / AOG</b> : null}
                {mission.hasPendingChange ? <b className="pending-change">Değişiklik onay bekliyor</b> : null}
              </header>
              <div className="mission-card__title">
                <span className="mission-card__icon"><BriefcaseBusiness /></span>
                <div>
                  <h2>{mission.revision.taskName || `${mission.revision.location} görevi`}</h2>
                  <p><MapPin size={16} />{mission.revision.location}</p>
                </div>
              </div>
              <dl className="mission-summary">
                <div><dt>Gidiş</dt><dd>{dateTimeLabel(mission.revision.outboundFlightAt)}</dd></div>
                <div><dt>{mission.actualReturnAt ? "Gerçek dönüş" : "Planlanan dönüş"}</dt><dd>{mission.actualReturnAt ? dateTimeLabel(mission.actualReturnAt) : mission.revision.returnUnknown ? "Belirsiz" : mission.revision.effectiveEndAt ? dateTimeLabel(mission.revision.effectiveEndAt) : "—"}</dd></div>
                {mission.durationMinutes !== null ? <div><dt>Görev süresi</dt><dd>{durationLabel(mission.durationMinutes)}</dd></div> : null}
                <div><dt>Uçak / motor</dt><dd>{equipmentLabel(mission.revision) || "Belirtilmedi"}</dd></div>
                <div><dt>Katılımcı</dt><dd>{mission.revision.participants.length} kişi</dd></div>
              </dl>
              <div className="mission-participant-chips">
                {mission.revision.participants.map((person) => <span key={String(person.id)}>{person.displayName}</span>)}
              </div>
              {mission.revision.publicNote ? <p className="mission-note">{mission.revision.publicNote}</p> : null}
              {mission.revision.managementNote ? <p className="mission-note mission-note--private"><ShieldCheck size={16} />{mission.revision.managementNote}</p> : null}
              <footer>
                {mission.canManage ? (
                  <Button variant="ghost" onClick={() => void openHistory(mission)}><History size={17} />Geçmiş</Button>
                ) : null}
                {(mission.canManage || mission.canEditOwn) && !["CANCELLED", "REJECTED"].includes(mission.lifecycle) ? (
                  <Button variant="secondary" onClick={() => openEdit(mission)}><Edit3 size={17} />Düzenle</Button>
                ) : null}
                {mission.lifecycle === "PENDING" && mission.canManage ? (
                  <Button onClick={() => { window.location.hash = "/approvals?kind=mission"; }}><Check size={17} />Onaya git</Button>
                ) : null}
                {(mission.canManage || (mission.canEditOwn && mission.lifecycle === "PENDING")) && !["CANCELLED", "REJECTED"].includes(mission.lifecycle) ? (
                  <Button variant="danger" onClick={() => { setActionReason(""); setAction({ mission, type: "cancel" }); }}><X size={17} />İptal et</Button>
                ) : null}
                {mission.canComplete ? (
                  <Button variant="ghost" onClick={() => { setActionReason(""); setActualReturnLocal(localInput(new Date().toISOString())); setAction({ mission, type: "complete" }); }}><UserRoundCheck size={17} />Görevi tamamla</Button>
                ) : null}
                {currentUser.role === "admin" ? (
                  <HardDeleteAction
                    kind="missions"
                    targetId={mission.id}
                    targetName={mission.revision.taskName || mission.revision.location}
                    onDeleted={load}
                  />
                ) : null}
              </footer>
            </Card>
          ))}
        </div>
      ) : (
        <Empty icon={<BriefcaseBusiness />} title="Görev bulunamadı" text={missions.length ? "Seçili filtrelerde görev kaydı yok." : "İlk saha görevini Yeni görev düğmesinden oluşturabilirsiniz."} />
      )}

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title={editing ? "Görevi düzenle" : "Yeni görev oluştur"} wide>
        <div className="mission-form form-stack">
          {!catalogs?.canManage ? (
            <Alert>Personel tarafından oluşturulan kayıt onaylanana kadar takvimi ve devamlılık hesabını etkilemez.</Alert>
          ) : null}
          <section className="mission-form__section">
            <h3><BriefcaseBusiness size={18} />Görev bilgileri</h3>
            <div className="form-grid form-grid--two">
              <Field label="Görev adı (isteğe bağlı)" value={form.taskName} maxLength={160} onChange={(event) => setField("taskName", event.target.value)} />
              <SelectField label="Görev türü" value={form.taskType} onChange={(event) => setField("taskType", event.target.value as "normal" | "aog")}>
                <option value="normal">Normal</option><option value="aog">Acil / AOG</option>
              </SelectField>
              <SearchableCombobox
                label="Ülke"
                value={form.countryCode}
                options={(catalogs?.countries ?? []).map((country) => ({ value: country.code, label: country.name }))}
                onChange={(value) => setForm((current) => ({ ...current, countryCode: value as string, provinceCode: "" }))}
                placeholder="Ülke ara veya seç"
                required
              />
              {form.countryCode === "TR" ? (
                <SearchableCombobox
                  label="İl"
                  value={form.provinceCode}
                  options={(catalogs?.provinces ?? []).map((province) => ({ value: province.code, label: province.name }))}
                  onChange={(value) => setField("provinceCode", value as string)}
                  placeholder="İl ara veya seç"
                  required
                />
              ) : <Alert>Yurtdışı görevlerinde şehir tutulmaz; vize uygunluğu personel beyanına göre uyarı üretir.</Alert>}
            </div>
          </section>

          <section className="mission-form__section">
            <h3><CalendarClock size={18} />Zaman planı</h3>
            <div className="form-grid form-grid--two">
              <Field label="Dış istasyona gidiş seferi (tarih ve saat)" type="datetime-local" value={form.outboundFlightLocal} onChange={(event) => setField("outboundFlightLocal", event.target.value)} required />
            </div>
          </section>

          <section className="mission-form__section">
            <h3><ShieldCheck size={18} />Uçak–motor paketi ve skill</h3>
            {form.aircraftPackageId === "__legacy__" ? <Alert tone="warning">Bu eski kaydın uçak–motor değeri merkezi paket kataloğuyla kesin eşleşmiyor. Kaydetmeden önce geçerli bir paket seçin.</Alert> : null}
            <div className="form-grid form-grid--two">
              <SearchableCombobox
                label="Uçak–motor yetkinlik paketi"
                value={form.aircraftPackageId}
                options={aircraftPackageOptions}
                onChange={(value) => {
                  setOverrideWarnings([]);
                  const packageId = value as string;
                  setForm((current) => ({
                    ...current,
                    aircraftPackageId: packageId,
                    aircraftType: "",
                    engineType: "",
                  }));
                }}
                placeholder="Profil kataloğundaki paketi ara veya seç"
              />
              <Field label="Uçak tescili (isteğe bağlı)" placeholder="TC-LXX" value={form.aircraftRegistration} onChange={(event) => setField("aircraftRegistration", event.target.value)} />
            </div>
            <SearchableCombobox
              label="Skill ihtiyacı (İsteğe bağlı)"
              multiple
              value={form.requiredSkills}
              options={(catalogs?.skills ?? []).map((skill) => ({ value: skill, label: skillLabel(skill) }))}
              onChange={(value) => setField("requiredSkills", value as string[])}
              placeholder="Skill ara veya seç"
            />
          </section>

          <section className="mission-form__section">
            <div className="mission-form__heading">
              <h3><UsersRound size={18} />Personel Bul</h3>
              <Button variant="secondary" onClick={() => void findCandidates()} loading={finding} disabled={!formReady}><Search size={17} />Personel Bul</Button>
            </div>
            {!formReady ? <Alert tone="warning">Öneri için lokasyon ve dış istasyona gidiş seferi tarih-saatini tamamlayın.</Alert> : null}
            {candidates.length ? (
              <div className="candidate-panel">
                <div className="candidate-tools">
                  <Field label="Aday ara" value={candidateSearch} placeholder="Ad, soyad veya sicil" onChange={(event) => setCandidateSearch(event.target.value)} />
                  <div className="candidate-unit-filter" role="group" aria-label="Personel birimi">
                    {([
                      ["ALL", "Tümü"],
                      ["COMPOSITE", "Kompozit"],
                      ["STRUCTURAL", "Yapısal"],
                    ] as const).map(([value, label]) => (
                      <button type="button" className={candidateUnit === value ? "active" : ""} aria-pressed={candidateUnit === value} onClick={() => setCandidateUnit(value)} key={value}>{label}</button>
                    ))}
                  </div>
                  <label className="check-row"><input type="checkbox" checked={showUnsuitable} onChange={(event) => setShowUnsuitable(event.target.checked)} /><span>Uygun olmayanları göster</span></label>
                  <strong className="candidate-total">{visibleCandidates.length} aday</strong>
                </div>
                {candidateVisaSections.map((section) => section.items.length ? (
                  <section className="candidate-visa-section" key={section.key}>
                    {section.label ? <h4 className={`candidate-visa-section__title candidate-visa-section__title--${section.key}`}>{section.key === "valid" ? <Check size={16} /> : <AlertTriangle size={16} />}{section.label} <small>{section.items.length}</small></h4> : null}
                    {(["CURRENT", "NEXT", "OTHER", "UNSUITABLE"] as const).map((group) => {
                      const groupItems = section.items.filter((candidate) => candidate.shiftGroup === group);
                      if (!groupItems.length) return null;
                      return (
                        <div className="candidate-group" key={`${section.key}-${group}`}>
                          <h4><ChevronDown size={16} />{groupLabels[group]} <small>{groupItems.length}</small></h4>
                          <div className="candidate-list">{groupItems.map(candidateCard)}</div>
                        </div>
                      );
                    })}
                  </section>
                ) : null)}
              </div>
            ) : null}
            <p className="selected-count"><UserRoundCheck size={17} />{form.participantIds.length} personel seçildi</p>
          </section>

          <section className="mission-form__section">
            <h3><Edit3 size={18} />Açıklamalar</h3>
            <TextareaField label="Ekip arkadaşlarının görebileceği kısa açıklama" value={form.publicNote} maxLength={1000} onChange={(event) => setField("publicNote", event.target.value)} />
            {catalogs?.canManage ? <TextareaField label="Yönetim özel notu" hint="Takvimde, normal personel API'sinde ve bildirimlerde gösterilmez." value={form.managementNote} maxLength={2000} onChange={(event) => setField("managementNote", event.target.value)} /> : null}
          </section>

          {overrideWarnings.length ? (
            <Alert tone="danger" title="Kaydetme anındaki güncel uyarılar">
              <ul>{overrideWarnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
            </Alert>
          ) : null}
          {overrideWarnings.length ? <TextareaField label="Uyarıları aşma gerekçesi" value={form.overrideReason} minLength={3} maxLength={1000} required onChange={(event) => setField("overrideReason", event.target.value)} /> : null}
          <div className="modal-actions">
            <Button variant="ghost" onClick={() => setFormOpen(false)}>Vazgeç</Button>
            <Button onClick={() => void save()} loading={saving} disabled={!formReady || !form.participantIds.length || (overrideWarnings.length > 0 && form.overrideReason.trim().length < 3)}>
              {catalogs?.canManage ? "Görevi kaydet" : "Onaya gönder"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={Boolean(action)} onClose={() => setAction(null)} title={action?.type === "cancel" ? "Görevi iptal et" : "Görevi tamamla"}>
        <div className="form-stack">
          <p>Bu işlem fiziksel kayıt silmez; görev geçmişi ve audit kaydı korunur.</p>
          {action?.type === "complete" ? (
            <>
              <Field label="Personelin dönüş tarihi ve saati" type="datetime-local" value={actualReturnLocal} max={localInput(new Date().toISOString())} required onChange={(event) => setActualReturnLocal(event.target.value)} />
              <Alert>Dönüş kaydedildiğinde görev süresi, dış istasyona gidiş ile bu tarih-saat arasından otomatik hesaplanır.</Alert>
              <TextareaField label="Tamamlama notu (isteğe bağlı)" value={actionReason} maxLength={1000} onChange={(event) => setActionReason(event.target.value)} />
            </>
          ) : (
            <TextareaField label="İptal gerekçesi" value={actionReason} minLength={3} maxLength={1000} required onChange={(event) => setActionReason(event.target.value)} />
          )}
          <div className="modal-actions"><Button variant="ghost" onClick={() => setAction(null)}>Vazgeç</Button><Button variant={action?.type === "cancel" ? "danger" : "primary"} loading={actionLoading} disabled={action?.type === "cancel" ? actionReason.trim().length < 3 : !actualReturnLocal} onClick={() => void runAction()}>{action?.type === "cancel" ? "İptal et" : "Dönüşü kaydet ve tamamla"}</Button></div>
        </div>
      </Modal>

      <Modal open={Boolean(historyMission)} onClose={() => setHistoryMission(null)} title="Görev işlem geçmişi" wide>
        {historyLoading ? <Loading label="Görev geçmişi yükleniyor" /> : historyMission?.events?.length ? (
          <div className="audit-list">
            {historyMission.events.map((event) => {
              const changeSummary = eventChangeSummary(event);
              return (
                <article key={event.id}>
                  <span className="audit-icon"><History /></span>
                  <div>
                    <strong>{eventLabels[event.eventType]}</strong>
                    <span>
                      {event.actorName ?? "Sistem"}{event.actorRole ? ` · ${event.actorRole}` : ""}
                      {event.actedAsDelegate ? ` · ${event.delegatedForName ?? "Başteknisyen"} adına vekâleten` : ""}
                    </span>
                    {changeSummary ? <small>{changeSummary}</small> : null}
                    {event.reason ? <small>Gerekçe: {event.reason}</small> : null}
                  </div>
                  <time>{dateTimeLabel(event.createdAt)}</time>
                </article>
              );
            })}
          </div>
        ) : <Empty icon={<History />} title="İşlem geçmişi yok" text="Bu görev için kaydedilmiş olay bulunamadı." />}
      </Modal>
    </div>
  );
}
